import React, {useEffect, useMemo, useState} from "react";
import {FiPlus} from "react-icons/fi";
import {useParams, useSearchParams} from "react-router-dom";
import {toast} from "react-toastify";
import {useTranslation} from "react-i18next";
import Select from "../../components/common/select/Select";
import styles from "./QuestionBasePage.module.css";
import {deleteQuestion, getQuestions} from "../../services/questionsApi";
import {getLastUsedSubject, setLastUsedSubject} from "../../services/meApi";
import TableEmptyState from "../../components/common/tableEmptyState/TableEmptyState";
import QuestionContent from "../../components/common/QuestionContent/QuestionContent";
import QuestionVideo from "../../components/common/QuestionVideo/QuestionVideo";
import QuestionExplanation from "../../components/common/QuestionExplanation/QuestionExplanation";
import ActionsMenu from "../../components/common/actionsMenu/ActionsMenu";
import QuestionFormModal from "../examBase/questionFormModal/QuestionFormModal";
import ConfirmDeleteModal from "../../components/common/deleteModal/ConfirmDeleteModal";
import AiQuestionGenerateModal from "../examBase/aiQuestionGenerateModal/aiQuestionGenerateModal";
import Loader from "../../components/common/Loader/Loader";

import {getSubjectsWithQuestionCount} from "../../services/subjectsApi";
import PageHeader from "../../components/common/pageheader/PageHeader";
import {getDifficulty} from "../../utils/difficulty.util";

const QuestionBasePage = () => {
    const {t} = useTranslation();
    const {id} = useParams();
    // The question bank route has no :id, so `id` is undefined → keep examBaseId undefined (not
    // Number(undefined) === NaN, which is falsy but renders as the text "NaN" inside child modals).
    const examBaseId = id ? Number(id) : undefined;
    const [searchParams] = useSearchParams();

    const [subjectIdFilter, setSubjectIdFilter] = useState("");

    const [subjects, setSubjects] = useState([]);
    const [questionTextFilter, setQuestionTextFilter] = useState("");
    const [subjectsLoading, setSubjectsLoading] = useState(false);
    const [appliedSearch, setAppliedSearch] = useState({subjectId: "", questionText: ""});

    const [pageNo, setPageNo] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [initialized, setInitialized] = useState(false);

    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);

    const [isAiModalOpen, setIsAiModalOpen] = useState(false);

    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [questionToDelete, setQuestionToDelete] = useState(null);

    const [deleting, setDeleting] = useState(false);
    const [forceDelete, setForceDelete] = useState(false);
    const [forceDeleteMessage, setForceDeleteMessage] = useState("");

    const fetchQuestions = async (overrides = {}) => {
        const sId = overrides.subjectId !== undefined ? overrides.subjectId : subjectIdFilter;
        const targetPage = overrides.pageNo !== undefined ? overrides.pageNo : pageNo;
        try {
            setLoading(true);
            setError("");
            const filters = {pageNo: targetPage, pageSize};
            if (sId) filters.subjectId = sId;
            if (questionTextFilter.trim()) filters.questionText = questionTextFilter.trim();
            setAppliedSearch({subjectId: String(sId || ""), questionText: questionTextFilter.trim() || ""});
            const raw = await getQuestions(filters);
            const data = Array.isArray(raw) ? raw : raw.content || []
            setQuestions(data);
            const total =
                typeof raw?.totalPages === "number" && raw.totalPages > 0
                    ? raw.totalPages
                    : Math.ceil(data.length / pageSize) || 1;

            setTotalPages(total);
            setTotalElements(typeof raw?.totalElements === "number" ? raw.totalElements : data.length);
        } catch (e) {
            const msg =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                e.message ||
                t("pages.questionBank.loadError");
            setQuestions([]);
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    // Resolve the initial subject filter: subject-context (?subjectId=) > most-recently-used > all-mixed.
    const init = async () => {
        const subs = await fetchSubjectsForSelect();
        const existsInSubs = (sid) => subs.some((s) => String(s.id) === String(sid));

        let initial = "";
        const contextSubjectId = searchParams.get("subjectId");
        if (contextSubjectId && existsInSubs(contextSubjectId)) {
            // Arrived from a subject context → pre-filter to it and remember it for next time.
            initial = String(contextSubjectId);
            persistMru(initial);
        } else {
            try {
                const mru = await getLastUsedSubject();
                if (mru?.subjectId != null && existsInSubs(mru.subjectId)) {
                    initial = String(mru.subjectId);
                }
            } catch {
                /* no MRU available → fall back to the all-subjects mixed view */
            }
        }

        setSubjectIdFilter(initial);
        setInitialized(true);
        fetchQuestions({subjectId: initial, pageNo: 1});
    };

    useEffect(() => {
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (initialized) fetchQuestions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNo]);

    const handleCreateClick = () => {
        setEditingQuestion(null);
        setIsModalOpen(true);
    };

    const handleAiCreateClick = () => {
        setIsAiModalOpen(true);
    };

    const handleEditClick = (question) => {
        setEditingQuestion(question);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingQuestion(null);
    };

    const handleCloseAiModal = () => {
        setIsAiModalOpen(false);
    };

    const handleDeleteClick = (question) => {
        setError("");
        setQuestionToDelete(question);
        setIsDeleteOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!questionToDelete) return;

        try {
            setDeleting(true);
            setError("");
            await deleteQuestion(questionToDelete.id, forceDelete);
            toast.success(t("pages.questionBank.deleteSuccess"));
            setIsDeleteOpen(false);
            setQuestionToDelete(null);
            setForceDelete(false);
            setForceDeleteMessage("");
            setDeleting(false);
            fetchQuestions();
        } catch (e) {
            if (e.status === 409) {
                setForceDelete(true)
                setForceDeleteMessage(e?.response?.data?.message)
                setDeleting(false);
                return
            }
            const msg =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                e.message ||
                t("pages.questionBank.deleteError");
            setError(msg);
            setIsDeleteOpen(false);
            setQuestionToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setIsDeleteOpen(false);
        setQuestionToDelete(null);
        setForceDelete(false);
        setForceDeleteMessage("");
    };

    const fetchSubjectsForSelect = async () => {
        try {
            setSubjectsLoading(true);
            setError("");
            const data = await getSubjectsWithQuestionCount();
            const list = Array.isArray(data) ? data : [];
            setSubjects(list);
            return list;
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data?.error || err.message || t("pages.questionBank.topicsLoadError");
            setError(msg);
            return [];
        } finally {
            setSubjectsLoading(false);
        }
    };

    const handleTopicCreated = (newTopic) => {
        if (newTopic?.id) {
            setSubjects((prev) => {
                const list = Array.isArray(prev) ? prev : [];
                if (list.some((s) => Number(s.id) === Number(newTopic.id))) return list;
                return [...list, newTopic];
            });
        }
        fetchSubjectsForSelect();
    };

    const persistMru = (subjectId) => {
        // Fire-and-forget: remembering the filter must never block or break the page.
        setLastUsedSubject(subjectId || null).catch(() => { /* non-fatal */ });
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setError("");
        persistMru(subjectIdFilter || null);
        if (pageNo !== 1) setPageNo(1);
        else fetchQuestions();
    };

    const handleShowAll = () => {
        setError("");
        setSubjectIdFilter("");
        persistMru(null);
        if (pageNo !== 1) setPageNo(1);
        else fetchQuestions({subjectId: ""});
    };

    const onSuccessCreateQuestion = (createdTopicId) => {
        fetchSubjectsForSelect();
        if (createdTopicId != null && createdTopicId !== "") {
            // After creating a question, jump the list to that question's topic so the new question is visible.
            const sid = String(createdTopicId);
            setSubjectIdFilter(sid);
            persistMru(sid);
            if (pageNo !== 1) setPageNo(1);
            else fetchQuestions({subjectId: sid, pageNo: 1});
        } else {
            fetchQuestions();
        }
    }

    const subjectOptions = useMemo(() => {
        return (Array.isArray(subjects) ? subjects : []).map((s) => ({
            id: s.id,
            title: s.title,
            questionCount: s.questionCount,
            parentId: s.parentId,
            parentName: s.parentName,
        }));
    }, [subjects]);

    // Total across all subjects (sum of per-subject counts) for the "· N total" part of the header.
    const totalAllQuestions = useMemo(
        () => subjectOptions.reduce((n, s) => n + (Number(s.questionCount) || 0), 0),
        [subjectOptions]
    );

    const appliedSubjectTitle = useMemo(
        () => subjectOptions.find((s) => String(s.id) === String(appliedSearch.subjectId))?.title || "",
        [subjectOptions, appliedSearch.subjectId]
    );

    const goToPrevPage = () => {
        setPageNo((prev) => Math.max(prev - 1, 1));
    };

    const goToNextPage = () => {
        if (pageNo < totalPages) {
            setPageNo((prev) => prev + 1);
        }
    };

    return (
        <div className={styles.container}>
            <PageHeader title={t("pages.questionBank.title")} />

            <form className={styles.filterForm} onSubmit={handleSearchSubmit}>
                <Select
                    className={styles.filterInput}
                    value={subjectIdFilter}
                    onChange={(e) => setSubjectIdFilter(e.target.value)}
                    options={[
                        { value: "", label: t("pages.questionBank.selectTopic") },
                        ...subjectOptions.map((s) => ({
                            value: s.id,
                            label: (s.parentName ? `${s.parentName} › ${s.title}` : s.title) + ' (' + s.questionCount + ')',
                        })),
                    ]}
                />
                <input
                    type="text"
                    placeholder={t("pages.questionBank.questionTextPlaceholder")}
                    className={styles.filterInput}
                    value={questionTextFilter}
                    onChange={(e) => setQuestionTextFilter(e.target.value)}
                />

                <button type="submit" className={styles.primaryButton}>
                    {t("common.search")}
                </button>

                <div className={styles.questionsBtn}>
                    <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleCreateClick}>
                        <FiPlus size={18}/>
                        <span>{t("pages.questionBank.newQuestion")}</span>
                    </button>
                    <button type="button" className={`${styles.btn} ${styles.btnSecondary}`}
                            onClick={handleAiCreateClick}>
                        <img src={process.env.PUBLIC_URL + "/star_emoji.png"} className={styles.icon} alt=""/>
                        <span>{t("pages.questionBank.aiGenerate")}</span>
                    </button>
                </div>
            </form>

            {initialized && (
                <div className={styles.filterBanner}>
                    <div className={styles.filterBannerMain}>
                        <span className={styles.filterBannerLabel}>{t("pages.questionBank.showing")}:</span>
                        <span className={styles.filterBannerSubject}>
                            {appliedSearch.subjectId ? appliedSubjectTitle : t("pages.questionBank.allSubjects")}
                        </span>
                        <span className={styles.filterBannerCounts}>
                            {appliedSearch.subjectId
                                ? `${t("pages.questionBank.filteredCount", {count: totalElements})} · ${t("pages.questionBank.totalCount", {total: totalAllQuestions})}`
                                : t("pages.questionBank.totalCount", {total: totalAllQuestions})}
                        </span>
                    </div>
                    {appliedSearch.subjectId && (
                        <button type="button" className={styles.showAllBtn} onClick={handleShowAll}>
                            {t("pages.questionBank.showAll")}
                        </button>
                    )}
                </div>
            )}

            {(loading || !initialized) && <Loader fullscreen/>}

            {error && !loading && <div className={styles.error}>{error}</div>}

            {initialized && !loading && questions.length === 0 && !error && (
                <TableEmptyState
                    title={appliedSearch.subjectId || appliedSearch.questionText ? t("pages.questionBank.noQuestionsFoundFiltered") : t("pages.questionBank.noQuestionsTitle")}
                    description={appliedSearch.subjectId || appliedSearch.questionText ? t("pages.questionBank.noQuestionsDescriptionFiltered") :
                        t("pages.questionBank.noQuestionsDescription")}
                    icon={appliedSearch.name || appliedSearch.description ? "search" : "inbox"}
                />
            )}

            {initialized && !loading &&
                questions.map((q, index) => {
                    const difficulty = getDifficulty(q.difficultyLevel);

                    return (
                        <div key={q.id} className={styles.questionCard}>
                            <div className={styles.questionTopRow}>
                                <div className={styles.questionText}>
                                    <span className={styles.qNumber}>{(pageNo - 1) * pageSize + index + 1}.</span>

                                    <div className={styles.qContent}>
                                        <QuestionContent text={q.text} />
                                    </div>
                                </div>

                                <div className={styles.questionRight}>
                                    <div className={styles.topActions}>
                                        <span
                                            className={`${styles.difficultyBadge} ${
                                                styles[difficulty?.name?.toLowerCase()]
                                            }`}
                                        >
                                      {difficulty?.label || ""}
                                    </span>

                                        <ActionsMenu
                                            onEdit={() => handleEditClick(q)}
                                            onDelete={() => handleDeleteClick(q)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {(() => {
                                // With a subject filter active, the subject is already implied — show only the
                                // topic. Browsing all subjects, show "Subject › Topic" so the origin is clear.
                                const path = appliedSearch.subjectId
                                    ? (q.topicTitle || "")
                                    : [q.parentName, q.topicTitle].filter(Boolean).join(" › ");
                                return path ? <div className={styles.questionSubjectPath}>{path}</div> : null;
                            })()}

                            {q.attachmentUrl && (
                                <img src={q.attachmentUrl} alt="question" className={styles.imagePreview}/>
                            )}

                            <QuestionVideo playbackUrl={q.videoPlaybackUrl} thumbnailUrl={q.videoThumbnailUrl}/>

                            <QuestionExplanation explanation={q.explanation}/>

                            <div className={styles.optionsList}>
                                {q.options?.map((opt, idx) => (
                                    <div
                                        key={opt.id || opt.optionText}
                                        className={`${styles.optionItem} ${opt.correct ? styles.correct : ""}`}
                                    >
                                        <span className={styles.optionNumber}>
                                          {String.fromCharCode(65 + idx)})
                                        </span>

                                        <div className={styles.optionContent}>
                                            <QuestionContent text={opt.optionText} />
                                            {opt.attachmentUrl && (
                                                <img
                                                    src={opt.attachmentUrl}
                                                    alt="option"
                                                    className={styles.imagePreview}
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })
            }

            <div className={styles.pagination}>
                <button type="button" onClick={goToPrevPage} disabled={pageNo === 1}>
                    {t("common.previous")}
                </button>

                <span>
              {t("common.pageOf", {page: pageNo, total: totalPages || 1})}
            </span>

                <button type="button" onClick={goToNextPage} disabled={pageNo >= totalPages}>
                    {t("common.next2")}
                </button>
            </div>

            <QuestionFormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                examBaseId={examBaseId}
                onSuccess={onSuccessCreateQuestion}
                mode={editingQuestion ? "edit" : "create"}
                initialQuestion={editingQuestion}
                subjectOptions={subjectOptions}
                onSubjectsChanged={handleTopicCreated}
            />

            <AiQuestionGenerateModal
                isOpen={isAiModalOpen}
                onClose={handleCloseAiModal}
                examBaseId={examBaseId}
                onSuccess={onSuccessCreateQuestion}
                subjectOptions={subjectOptions}
                onSubjectsChanged={handleTopicCreated}
            />

            <ConfirmDeleteModal
                isOpen={isDeleteOpen}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                message={
                    forceDelete
                        ? forceDeleteMessage
                        : (questionToDelete?.videoUid
                            ? t("pages.questionBank.deleteConfirmWithVideo", {
                                minutes: Math.ceil((questionToDelete.videoDurationSeconds || 0) / 60),
                            })
                            : t("pages.questionBank.deleteConfirm"))
                }
                disabled={deleting}
                forceDelete={forceDelete}
            />
        </div>
    );
};

export default QuestionBasePage;
